import type { SalesWorkspaceContext } from './sales-collaboration';
import type { SalesLocationCallbackStatus, SalesLocationInterestStatus } from './sales-collaboration';

export interface SalesLocationSummaryRequestLocation {
  feature_identity_key: string;
  location_id?: string | null;
  display_name?: string;
  address_line1?: string;
  locality?: string;
  region_code?: string;
  postal_code?: string;
  country_code?: string;
}

export interface SalesLocationRelationshipSummary {
  feature_identity_key: string;
  relationship_state: string;
  has_existing_relationship: boolean;
  has_active_service: boolean;
  active_service_count: number;
  relationship_count: number;
  do_not_contact: boolean;
  follow_up_required: boolean;
  follow_up_due_at?: string | null;
  interest_status?: SalesLocationInterestStatus | null;
  callback_status?: SalesLocationCallbackStatus | null;
  note_count: number;
  cooldown_active?: boolean;
  cooldown_expires_at?: string | null;
  cooldown_blocks_current_context?: boolean;
  cooldown_enforcement_mode?: string | null;
  cooldown_owner_user_id?: string | null;
  cooldown_owner_email?: string | null;
  cooldown_owner_username?: string | null;
  cooldown_owner_seller_tenant_id?: string | null;
  cooldown_owner_entitlement_id?: string | null;
}

export interface SalesLocationQuoteListItem {
  quote_id: string;
  status: string;
  quote_origin?: string;
  created_at: string;
  updated_at: string;
  converted_order_id?: string | null;
}

export interface SalesLocationOrderListItem {
  order_id: string;
  friendly_order_id: string;
  status: string;
  lifecycle_phase: string;
  created_at: string;
  updated_at: string;
}

export interface SalesLocationQuoteSummary {
  feature_identity_key: string;
  open_quote_count: number;
  total_quote_count: number;
  latest_quote_at?: string | null;
  latest_quote?: SalesLocationQuoteListItem | null;
}

export interface SalesLocationOrderSummary {
  feature_identity_key: string;
  priority_order_count: number;
  active_order_count: number;
  total_order_count: number;
  latest_order_at?: string | null;
  latest_order?: SalesLocationOrderListItem | null;
}

export interface SalesLocationSummaryItem {
  feature_identity_key: string;
  location_relationship_summary: SalesLocationRelationshipSummary;
  quote_summary: SalesLocationQuoteSummary;
  order_summary: SalesLocationOrderSummary;
  recent_quotes: SalesLocationQuoteListItem[];
  recent_orders: SalesLocationOrderListItem[];
}

export interface SalesLocationSummariesResponse {
  items: SalesLocationSummaryItem[];
}

export interface LoadSalesLocationSummariesOptions {
  collaboration_signal_only?: boolean;
}

export async function loadSalesLocationSummaries(
  locations: SalesLocationSummaryRequestLocation[],
  context?: SalesWorkspaceContext | null,
  options?: LoadSalesLocationSummariesOptions | null,
): Promise<SalesLocationSummariesResponse> {
  const response = await fetch('/api/v1/sales/location-summaries', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      locations,
      catalog_owner_tenant_id: context?.catalog_owner_tenant_id,
      entitlement_id: context?.entitlement_id,
      collaboration_signal_only: options?.collaboration_signal_only ?? false,
    }),
  });

  if (!response.ok) {
    const fallback = 'Failed to load sales location summaries';
    let detail = '';
    try {
      detail = (await response.text()).trim();
    } catch {
      detail = '';
    }
    throw new Error(detail || fallback);
  }

  const payload = (await response.json()) as SalesLocationSummariesResponse | null;
  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
  };
}
